import React, {useEffect, useState} from 'react';
import {css} from '@emotion/css';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Modal from 'patient-ping-remedy/packages/modal';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import ClientDropdownList from '@clients/referral/ClientDropdownList';
import {getEnv, useStateConfig} from '@/hooks/useStateConfig';
import {useAuthStore} from '@/store/auth_store';
import {useRosterStore} from '@/store/roster_store';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import {useOpenBeds} from '@/hooks/useOpenBeds';
import {useFindHelp} from '@/hooks/useFindHelp';
import Validate from '@/helpers/validate';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import {useRoundTrip} from "@/hooks/useRoundTrip";
import {
  SSOApplication,
  SSOLaunchMethod
} from "@/api/dto/mixpanel";
import {DataPermissionLevel, DataPermissionLevelNew, DataSource} from "@/api/dto/permissions";
import {FeatureToggle} from "@/api/dto/dto";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";

type Props = {
  hideModal(): void;
}

export default function CreateReferralSso(props: Props) {
  const stateConfig = useStateConfig();
  const { currentUser } = useAuthStore();
  const { currentRoster } = useRosterStore();
  const { openOpenBeds } = useOpenBeds();
  const { openRoundTrip } = useRoundTrip();
  const { openFindHelp } = useFindHelp();
  const {checkBooleanToggle} = useFeatureToggles();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS) ?? false;

  const [isFindHelpSelected, setIsFindHelpSelected] = useState(false);

  const { form, isValid, setInitialForm, handleChange, handleSubmit } = useForm(async () => {
    const callbacks: [string[], () => Promise<void>][] = [
      [
        [DataSource.OB_REFERRALS, DataSource.OB_CRISIS],
        async () => {
          const applicationName = form.type.value.value === DataSource.OB_REFERRALS ?
            SSOApplication.OPENBEDS_REFERRALS : SSOApplication.OPENBEDS_CRISIS;

          await openOpenBeds(applicationName, SSOLaunchMethod.CREATE_BUTTON);
        },
      ],
      [
        ['ROUNDTRIP'],
        openRoundTrip,
      ],
      [
        [DataSource.FINDHELP],
        () => openFindHelp(currentRoster!.id, form.client?.value?.value),
      ],
    ];

    const [_, callback] = callbacks.find(([types]) =>
      types.includes(form.type.value.value)) ?? [];

    if (callback) {
      props.hideModal();
      await callback();
    }
  });

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        'type',
        null,
        { valid: false, message: '' },
        [Validate.required],
        true,
      ),
      ...createFormFieldConfig(
        'client',
        null,
        { valid: false, message: '' },
        [Validate.required],
        true,
      ),
    });
  }, [setInitialForm]);

  const handleTypeChange = (value: Value | null) => {
    const isFindHelpSelected = !!value &&
      [DataSource.FINDHELP.toString()].includes(value.value);
    setIsFindHelpSelected(isFindHelpSelected);

    form.client.valid = { valid: !isFindHelpSelected || form.client.value, message: '' };

    handleChange(null, 'type', value);
  };

  const typeOptions = [
    {
      available: !!getEnv(stateConfig?.api?.openBeds),
      label: stateConfig?.branding.openBeds.referral.service,
      level: DataPermissionLevelNew.SSO,
      oldLevel: DataPermissionLevel.SSO,
      permission: DataSource.OB_REFERRALS,
    },
    {
      available: !!getEnv(stateConfig?.api?.openBeds),
      label: stateConfig?.branding.openBeds.crisis.service,
      level: DataPermissionLevelNew.SSO,
      oldLevel: DataPermissionLevel.SSO,
      permission: DataSource.OB_CRISIS,
    },
    {
      available: !!getEnv(stateConfig?.api?.findHelp)?.baseUrl,
      label: stateConfig?.branding.findHelp.service,
      level: DataPermissionLevelNew.SSO,
      oldLevel: DataPermissionLevel.SSO,
      permission: DataSource.FINDHELP,
    },
  ].filter((option) =>
    option.available &&
    (isNewPermissionsEnabled ?
      PermissionsHelpers.canForUserDataPermissionNew(currentUser!, option.level, option.permission) :
      PermissionsHelpers.canForUserDataPermission(currentUser!, option.oldLevel, option.permission))
  ).map((option): Value => ({
    label: option.label ?? '',
    value: option.permission,
  }));

  if (currentUser?.roundtrip_sso || PermissionsHelpers.hasAdminRole(currentUser!)) {
    typeOptions.push({
      label: stateConfig?.branding.roundTrip.service ?? '',
      value: 'ROUNDTRIP',
    });
  }

  const modalButtons = [
    <Button
      key="submit"
      styleType={StyleType.PRIMARY}
      disabled={!isValid()}
      onClick={handleSubmit}
    >
      Create
    </Button>,
    <Button key="cancel" styleType={StyleType.TERTIARY} onClick={props.hideModal}>
      Cancel
    </Button>,
  ];

  return (
    <Modal
      headerText="Create Referrals SSO"
      buttons={modalButtons}
      className={css({ width: '70rem' })}
    >
      <DropdownList
        id="referral-type"
        label="Type"
        items={typeOptions}
        value={form.type?.value}
        handleChange={handleTypeChange}
        error={!form.type?.valid?.valid && form.type?.valid?.message}
        errorMessage={form.type?.valid?.message}
        required
        isWithinModal
      />
      {isFindHelpSelected && (
        <ClientDropdownList
          id="referral-client"
          form={form}
          handleChange={handleChange}
          required
          isWithinModal
        />
      )}
    </Modal>
  );
}
